import { useId, useSyncExternalStore, type ReactNode } from "react";
import { aufgeklappt, type AufklappWahl } from "../navigation";
import { wahlLesen, wahlMerken } from "../speicher";
import s from "./shell.module.css";

/* Aufklappbarer Bereich der Seitenleiste (BAUPLAN-SEITENLEISTE.md, Abschnitt 5): die Redaktion. Kopf als Knopf mit Pfeil, zugeklappt
   rechts die Zahl der Einträge. Die Wahl merkt sich das Gerät. Seitenleiste und Blatt „Mehr“ zeigen denselben Zustand. */

const schluessel = (id: string) => `navigation.${id}`;
const hoerer = new Set<() => void>();

function lesen(id: string): AufklappWahl {
  const w = wahlLesen(schluessel(id));
  return w === "offen" || w === "zu" ? w : null;
}

function abonnieren(neu: () => void): () => void {
  hoerer.add(neu);
  // Auch ein anderer Tab kann die Wahl ändern
  window.addEventListener("storage", neu);
  return () => {
    hoerer.delete(neu);
    window.removeEventListener("storage", neu);
  };
}

/** Gemerkte Wahl je Bereich, null ohne Wahl */
export function useAufklappWahl(id: string): AufklappWahl {
  return useSyncExternalStore(abonnieren, () => lesen(id));
}

export function aufklappMerken(id: string, offen: boolean): void {
  wahlMerken(schluessel(id), offen ? "offen" : "zu");
  hoerer.forEach((h) => h());
}

const PFEIL = (
  <svg className={s.pfeil} viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
    <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export function Aufklappbar({
  id,
  titel,
  anzahl,
  aktivDrin,
  children,
}: {
  id: string;
  titel: string;
  /** Zahl der Einträge, steht zugeklappt rechts */
  anzahl: number;
  /** Die aktuelle Seite liegt in diesem Bereich */
  aktivDrin: boolean;
  children: ReactNode;
}) {
  const inhalt = useId();
  const offen = aufgeklappt(useAufklappWahl(id), aktivDrin);
  return (
    <nav aria-label={titel}>
      <button
        type="button"
        className={s.aufklappen}
        aria-expanded={offen}
        aria-controls={inhalt}
        data-aktiv={!offen && aktivDrin ? true : undefined}
        onClick={() => aufklappMerken(id, !offen)}
      >
        {PFEIL}
        {titel}
        {offen ? null : (
          <span className={s.anzahl}>
            <span aria-hidden="true">{anzahl}</span>
            <span className="nur-leser">, {anzahl === 1 ? "ein Eintrag" : `${anzahl} Einträge`}</span>
          </span>
        )}
      </button>
      <div id={inhalt} hidden={!offen}>
        {children}
      </div>
    </nav>
  );
}
